import { useEffect, useState } from "react";
import { api, fmtNGN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Users, Gift, Share2, TrendingUp, CheckCircle2, Link2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const link = `${window.location.origin}/register?ref=${data.referral_code}`;
  const copyLink = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied!"); };
  const share = async () => {
    const text = `Join Art Review Club with my code ${data.referral_code} and get ₦500 Welcome Cash-back! ${link}`;
    if (navigator.share) { try { await navigator.share({ title: "Art Review Club", text, url: link }); } catch { /* cancelled */ } }
    else { navigator.clipboard.writeText(text); toast.success("Invite copied to clipboard!"); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold">Refer & Earn</h1>
        <p className="text-sm text-muted-foreground mt-1">Earn <b className="text-foreground">{data.commission_pct}% commission</b> on every artwork your referrals purchase — credited to your Reward Wallet. Refer as many people as you like.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-primary text-primary-foreground rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 h-40 w-40 navy-gradient opacity-30 blur-2xl rounded-full" />
          <div className="flex items-center gap-2 text-primary-foreground/70 text-sm relative"><Gift className="h-4 w-4" /> Your Referral Code</div>
          <p className="font-heading text-4xl font-bold tracking-wider mt-2 relative" data-testid="referral-code">{data.referral_code}</p>
          <div className="flex flex-wrap gap-2 mt-5 relative">
            <Button variant="secondary" className="rounded-full" onClick={copyLink} data-testid="copy-link-btn"><Link2 className="h-4 w-4 mr-2" /> Link</Button>
            <Button className="rounded-full bg-white text-primary hover:bg-white/90 font-semibold" onClick={share} data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Share</Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-3xl p-6 flex flex-col items-center justify-center">
          <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground font-bold mb-3">Scan to Join</p>
          <div className="bg-white p-3 rounded-2xl" data-testid="referral-qr">
            <QRCodeCanvas value={link} size={140} bgColor="#ffffff" fgColor="#0B1D51" level="M" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat icon={Users} value={data.count} label="Referrals" testid="referral-count" />
        <Stat icon={CheckCircle2} value={data.purchased_count} label="Purchased" testid="referral-purchased" />
        <Stat icon={TrendingUp} value={fmtNGN(data.total_earned)} label="Earned" testid="referral-earned" small />
      </div>

      <div className="bg-card border border-border rounded-3xl p-6" data-testid="referral-earnings-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-bold">Earnings Over Time</h3>
            <p className="text-xs text-muted-foreground">Cumulative referral commission credited to your Reward Wallet.</p>
          </div>
          <TrendingUp className="h-5 w-5 text-gold" />
        </div>
        {data.earnings_series?.length ? (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.earnings_series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="earnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C8A951" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#C8A951" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d?.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₦${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} axisLine={false} tickLine={false} width={48} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                  formatter={(v, name) => [fmtNGN(v), name === "cumulative" ? "Total earned" : "That day"]}
                  labelFormatter={(l) => new Date(l).toLocaleDateString()}
                />
                <Area type="monotone" dataKey="cumulative" stroke="#C8A951" strokeWidth={2.5} fill="url(#earnGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-40 grid place-items-center text-center">
            <p className="text-sm text-muted-foreground">No earnings yet. Your referral earnings will appear here as your downline buys artworks.</p>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold mb-3">Reward History</h3>
        {data.reward_history?.length ? (
          <div className="space-y-2" data-testid="referral-history">
            {data.reward_history.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
                <div><p className="text-sm font-medium">{t.description}</p><p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p></div>
                <span className="font-bold text-gold">+{fmtNGN(t.amount)}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No referral rewards yet. Share your code to start earning 3% on your downline's purchases!</p>}
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold mb-3">Your Invites</h3>
        {data.invited.length ? (
          <div className="space-y-2">
            {data.invited.map((u, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
                <span className="font-medium">{u.username}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.status === "active" ? "bg-gold/15 text-gold" : "bg-secondary text-muted-foreground"}`}>{u.status === "active" ? "Active" : "Pending"}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No invites yet. Share your code to start earning!</p>}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, testid, small }) {
  return (
    <div className="bg-card border border-border rounded-3xl p-5">
      <Icon className="h-5 w-5 text-gold" />
      <p className={`font-heading font-bold mt-2 ${small ? "text-xl" : "text-3xl"}`} data-testid={testid}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
